import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { hasModifierKey } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  HostListener,
  InjectionToken,
  Input,
  Optional,
  Output,
} from '@angular/core';

import { isExperimental } from '@lockton-apps/shared/util/helpers';

import { SelectBoxContentDirective } from './select-box-content.directive';
import { SelectBoxGroupComponent } from './select-box-group/select-box-group.component';
import { firstValueFrom, Observable, of } from 'rxjs';
import { constant } from 'lodash';

export class SelectBoxSelectionChange<T = unknown> {
  constructor(
    /** Reference to the option that emitted the event. */
    public source: SelectBoxComponent<T>,
    /** Whether the change in the option's value was a result of a user action. */
    public isUserInput = false
  ) {}
}

export const SELECT_BOX_PARENT_COMPONENT =
  new InjectionToken<SelectBoxGroupComponent>('SELECT_BOX_PARENT_COMPONENT');

@Component({
    selector: 'lui-select-box',
    templateUrl: './select-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectBoxComponent<T = unknown> {
  @HostBinding('attr.role') role = 'option';

  @HostBinding('tabindex') get tabIndex() {
    return this.disabled ? -1 : 0;
  }

  @HostBinding('class') className = 'lui-select-box';

  @HostBinding('class.lui-select-box-with-content') get hasContent() {
    return this.selectBoxContent !== undefined;
  }

  @HostBinding('class.lui-select-box-selected')
  _selected = false;

  @HostBinding('class.lui-select-box-readonly')
  get readonlyClass() {
    return this.readonly;
  }

  @HostBinding('class.disabled') get disabledClass() {
    return this.disabled;
  }

  @HostBinding('attr.aria-selected') get ariaSelected() {
    return this._selected;
  }

  @Input() get checked() {
    return this._selected;
  }
  set checked(value: BooleanInput) {
    this._selected = coerceBooleanProperty(value);
  }

  @Input() get disabled() {
    return this._disabled || this._disabledStateOverride;
  }

  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
  }

  @Input() value: unknown;
  @Input() icon!: string;
  @Input() beforeSelect: (value: unknown) => Observable<boolean> = constant(
    of(true)
  );

  @Input() id!: string;
  @Input() readonly: BooleanInput;

  @ContentChild(SelectBoxContentDirective, { static: true })
  selectBoxContent!: SelectBoxContentDirective;

  @Output() selectionChange = new EventEmitter<SelectBoxSelectionChange<T>>();

  private _disabled = false;
  private _disabledStateOverride = false;

  @HostListener('click') onOptionSelected(): void {
    if (!this.readonly) {
      this._selectViaInteraction();
    }
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent): void {
    if (!this.readonly) {
      this._handleKeydown(e);
    }
  }

  constructor(
    private cd: ChangeDetectorRef,
    @Optional() private selectboxgroup: SelectBoxGroupComponent
  ) {}

  toggle(): void {
    this._selected = !this._selected;
  }

  select(): void {
    if (!this._selected) {
      this._selected = true;
      this.cd.markForCheck();
      this._emitSelectionChangeEvent();
    }
  }

  deselect(): void {
    if (this._selected) {
      this._selected = false;
      this.cd.markForCheck();
      this._emitSelectionChangeEvent();
    }
  }

  toggleDisableStateOverride(value: boolean): void {
    this._disabledStateOverride = value ?? !this._disabledStateOverride;
  }

  /** Ensures the option is selected when activated from the keyboard. */
  _handleKeydown(event: KeyboardEvent): void {
    if (
      (event.code === 'Enter' || event.code === 'Space') &&
      !hasModifierKey(event)
    ) {
      this._selectViaInteraction();

      // Prevent the page from scrolling down and form submits.
      event.preventDefault();
    }
  }

  /**
   * `Selects the option while indicating the selection came from the user. Used to
   * determine if the select's view -> model callback should be invoked.`
   */
  async _selectViaInteraction(): Promise<void> {
    const canChange: boolean = await firstValueFrom(
      this.beforeSelect(this.value)
    );

    if (!this.disabled && canChange) {
      if (
        // EXPERIMENTAL:BUG:LCP-800
        isExperimental() &&
        !!this.selectboxgroup &&
        !this.selectboxgroup.canDeselect &&
        this._selected
      )
        return;
      this._selected = !this._selected;
      this._emitSelectionChangeEvent(true);
    }
  }

  /** Emits the selection change event. */
  private _emitSelectionChangeEvent(isUserInput = false): void {
    this.selectionChange.emit(
      new SelectBoxSelectionChange<T>(this, isUserInput)
    );
  }
}
